import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { CardDuelStar } from '@/components/ui/card';
import { FiX } from 'react-icons/fi';

const ChallengeModal = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-modal', 'flex h-0 flex-grow flex-col', className)}
    {...props}
  />
));
ChallengeModal.displayName = 'ChallengeModal';

const ChallengeModalHat = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'flex min-w-[194px] max-w-full flex-col items-center justify-center gap-2 truncate rounded-b-xl shadow-dropshadow',
      'border-x-2 border-b-2 border-solid border-neutral-lightest bg-white px-4 py-2',
      'text-sm font-bold uppercase text-primary',
      className
    )}
    {...props}
  />
));
ChallengeModalHat.displayName = 'ChallengeModalHat';

const challengeModalSectionVariants = cva('relative flex-grow overflow-hidden overflow-y-auto', {
  variants: {
    variant: {
      name: ['flex flex-col items-center gap-6 bg-bright px-4 pb-4'],
      question: ['flex flex-col gap-5 bg-white px-4 py-7 shadow-dropshadow-lg', 'md:px-[2.6vw]', 'animate-classic-fade-in'],
      success: ['flex flex-col items-center gap-6 bg-bright px-4 pb-4', 'animate-classic-fade-in']
    }
  },
  defaultVariants: {
    variant: 'name'
  }
});

const ChallengeModalSection = React.forwardRef(({ className, variant, ...props }, ref) => (
  <section
    ref={ref}
    className={cn('c-challenge-modal-section', challengeModalSectionVariants({ variant }), className)}
    {...props}
  />
));
ChallengeModalSection.displayName = 'ChallengeModalSection';

const ChallengeModalSectionNameHeader = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col items-center gap-2 text-center', className)}
    {...props}
  />
));
ChallengeModalSectionNameHeader.displayName = 'ChallengeModalSectionNameHeader';

const ChallengeModalSectionNameTitle = React.forwardRef(({ className, variant, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('mb-0 text-[1.75rem] font-bold text-primary', className)}
    {...props}
  />
));
ChallengeModalSectionNameTitle.displayName = 'ChallengeModalSectionNameTitle';

const ChallengeModalSectionNameSubtitle = React.forwardRef(({ className, variant, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('mb-0 flex items-center gap-1 text-sm font-light normal-case text-neutral [&>svg]:w-4 [&>svg]:shrink-0', className)}
    {...props}
  />
));
ChallengeModalSectionNameSubtitle.displayName = 'ChallengeModalSectionNameSubtitle';

const ChallengeModalWarning = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'absolute left-0 top-[60px] inline-flex max-w-[204px] animate-slide-in-left items-center gap-4 rounded-r-lg bg-[#FAFAFA] p-5 text-base font-bold leading-tight text-neutral-dark shadow-[0px_4px_4px_0px_rgba(114,114,114,0.10)] md:bottom-[60px] md:top-auto',
      '[&_img]:h-auto [&_img]:w-6',
      className
    )}
    {...props}
  />
));
ChallengeModalWarning.displayName = 'ChallengeModalWarning';

const ChallengeModalHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between', className)}
    {...props}
  />
));
ChallengeModalHeader.displayName = 'ChallengeModalHeader';

const ChallengeModalHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('', className)}
    {...props}
  />
));
ChallengeModalHeaderContent.displayName = 'ChallengeModalHeaderContent';

const ChallengeModalTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('mb-0 text-xl font-bold leading-[1.2] text-primary', className)}
    {...props}
  />
));
ChallengeModalTitle.displayName = 'ChallengeModalTitle';

const ChallengeModalSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mb-0 text-sm font-light text-neutral', className)}
    {...props}
  />
));
ChallengeModalSubtitle.displayName = 'ChallengeModalSubtitle';

const ChallengeModalDuel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-duel',
      // Estilos base
      'grid grid-cols-3 items-center gap-2',
      // Estilos para desktop
      'md:gap-6',
      'mx-auto mb-8',
      className
    )}
    {...props}
  />
));
ChallengeModalDuel.displayName = 'ChallengeModalDuel';

const ChallengeModalStar = React.forwardRef(({ className, ...props }, ref) => (
  <CardDuelStar
    ref={ref}
    className={cn(
      'c-challenge-star',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
ChallengeModalStar.displayName = 'ChallengeModalStar';

const ChallengeModalProfile = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-profile',
      // Estilos de layout
      'flex flex-col items-center',
      'z-10 text-[#5d6a86]',
      className
    )}
    {...props}
  />
));
ChallengeModalProfile.displayName = 'ChallengeModalProfile';

const ChallengeModalAvatar = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-avatar',
      // Estilos de layout
      'relative',
      className
    )}
    {...props}
  />
));
ChallengeModalAvatar.displayName = 'ChallengeModalAvatar';

const ChallengeModalName = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-challenge-modal-name',
      // Estilos de layout
      '',
      className
    )}
    {...props}
  />
));
ChallengeModalName.displayName = 'ChallengeModalName';

const ChallengeModalScore = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-score',
      // Estilos base
      'flex items-center gap-2',
      'text-[28px] font-bold',
      'mx-auto',
      // Estilos para desktop
      'md:gap-8',
      className
    )}
    {...props}
  />
));
ChallengeModalScore.displayName = 'ChallengeModalScore';

const ChallengeModalVs = React.forwardRef(({ className, ...props }, ref) => (
  <FiX
    ref={ref}
    className={cn('c-challenge-modal-vs', 'text-danger', className)}
    {...props}
  />
));
ChallengeModalVs.displayName = 'ChallengeModalVs';

const ChallengeModalNumber = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-challenge-modal-number',
      // Estilos base
      className
    )}
    {...props}
  />
));
ChallengeModalNumber.displayName = 'ChallengeModalNumber';

const ChallengeModalCrown = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('c-challenge-modal-crown', 'absolute left-0 top-[-20px] h-[40px] w-[40px] rotate-[-20deg]', className)}
    {...props}
  />
));
ChallengeModalCrown.displayName = 'ChallengeModalCrown';

const ChallengeModalResult = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-result',
      // Estilo base
      'flex w-full flex-col gap-6',
      'md:w-4/5',
      className
    )}
    {...props}
  />
));
ChallengeModalResult.displayName = 'ChallengeModalResult';

const ChallengeModalImgLeaf = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn(
      'c-challenge-modal-img-leaf',
      // Estilo base
      'absolute top-[-20px] h-[120px]',
      className
    )}
    {...props}
  />
));
ChallengeModalImgLeaf.displayName = 'ChallengeModalImgLeaf';

const ChallengeModalImgStar = React.forwardRef(({ className, variant = 1, ...props }, ref) => {
  const getDelay = {
    1: '1s',
    2: '0s',
    3: '1.5s',
    4: '0.5s'
  };

  return (
    <img
      ref={ref}
      className={cn(
        'c-challenge-modal-img-star',
        // Estilo base
        'absolute animate-pulse',
        variant === 1 && 'left-[-20px] top-[-10px] h-[25px] w-[25px]',
        variant === 2 && 'left-[-25px] top-[15px] h-[15px] w-[15px]',
        variant === 3 && 'right-[-20px] top-[-10px] h-[25px] w-[25px]',
        variant === 4 && 'right-[-25px] top-[15px] h-[15px] w-[15px]',
        className
      )}
      style={{ animationDelay: getDelay[variant] }}
      {...props}
    />
  );
});
ChallengeModalImgStar.displayName = 'ChallengeModalImgStar';

const ChallengeModalResultBg = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-result-bg',
      // Estilos de layout
      'relative flex h-[220px] items-center justify-center',
      // Estilos de borda e visualização
      'overflow-hidden rounded-[10px] border-[8px] border-solid border-white bg-[#f5f5f5]',
      className
    )}
    {...props}
  >
    <div
      className="absolute inset-0 z-0"
      style={{
        background: 'repeating-conic-gradient(from 0deg, #ffffff 0deg, #ffffff 10deg, #f5f5f5 10deg, #f5f5f5 20deg)'
      }}
    />
    {props.children}
  </div>
));
ChallengeModalResultBg.displayName = 'ChallengeModalResultBg';

const ChallengeModalResultEmpty = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-challenge-modal-result-empty',
      // Estilo base
      'flex h-[80px] w-[80px] items-center justify-center',
      'rounded-full bg-[#ccd2e0] text-center text-[60px] font-bold text-white',
      'z-[1]',
      className
    )}
    {...props}
  />
));
ChallengeModalResultEmpty.displayName = 'ChallengeModalResultEmpty';

const ChallengeModalResultSection = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-result-section',
      // Estilo base
      'max-h-[300px] overflow-y-auto rounded-[10px] bg-white p-5',
      className
    )}
    {...props}
  />
));
ChallengeModalResultSection.displayName = 'ChallengeModalResultSection';

const ChallengeModalResultTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-challenge-modal-result-title',
      // Estilo base
      'rounded-[10px] bg-[#fafafa] p-3 text-center text-base font-bold',
      className
    )}
    {...props}
  />
));
ChallengeModalResultTitle.displayName = 'ChallengeModalResultTitle';

const ChallengeModalResultItems = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-result-items',
      // Estilo base
      'grid grid-cols-2',
      // Responsivo
      'max-md:gap-3 md:grid-cols-2',
      'max-md:grid-cols-1',
      className
    )}
    {...props}
  />
));
ChallengeModalResultItems.displayName = 'ChallengeModalResultItems';

const ChallengeModalResultItem = React.forwardRef(({ className, hasBg, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-modal-result-item',
      // Estilo base
      'flex flex-col justify-center gap-1',
      '[&_img]:mr-1 [&_img]:h-[25px] [&_img]:w-[25px]',
      '[&_.c-badge]:ml-6 [&_.c-badge]:w-fit',
      '[&_span]:ml-6 [&_span]:w-fit',
      hasBg && 'mb-3 rounded-[10px] bg-[#fafafa] p-2',
      className
    )}
    {...props}
  />
));
ChallengeModalResultItem.displayName = 'ChallengeModalResultItem';

export {
  ChallengeModal,
  ChallengeModalHat,
  ChallengeModalWarning,
  ChallengeModalHeader,
  ChallengeModalHeaderContent,
  ChallengeModalTitle,
  ChallengeModalSubtitle,
  ChallengeModalSection,
  ChallengeModalSectionNameHeader,
  ChallengeModalSectionNameTitle,
  ChallengeModalSectionNameSubtitle,
  ChallengeModalDuel,
  ChallengeModalStar,
  ChallengeModalProfile,
  ChallengeModalAvatar,
  ChallengeModalName,
  ChallengeModalScore,
  ChallengeModalVs,
  ChallengeModalNumber,
  ChallengeModalCrown,
  ChallengeModalImgLeaf,
  ChallengeModalImgStar,
  ChallengeModalResult,
  ChallengeModalResultBg,
  ChallengeModalResultEmpty,
  ChallengeModalResultSection,
  ChallengeModalResultTitle,
  ChallengeModalResultItems,
  ChallengeModalResultItem
};
